import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// =============================================================================
// Process Graphic Animations - Desktop
// =============================================================================
export function processAnimations() {
  const processGraphic = document.querySelector('.process__graphic')
  const processColumns = document.querySelectorAll('.process__graphic-column')

  if (processColumns.length === 0) return

  // Calculate and preserve the total height to prevent layout shift
  const originalHeight = processGraphic.offsetHeight
  gsap.set(processGraphic, { height: originalHeight })

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: '.process__graphic',
      start: 'top 75%',
      toggleActions: 'play none none none'
    }
  })

  // Phase 1: All THs stagger in quickly
  const thStagger = 0.2 // Fast stagger between columns
  const lastThStart = (processColumns.length - 1) * thStagger
  const bodyStartTime = lastThStart + 0.5 // Tbody starts right as last TH is settling in

  processColumns.forEach((column, columnIndex) => {
    const title = column.querySelector('.process__graphic-title')
    const titleIcon = column.querySelector('.process__graphic-title-icon')
    const body = column.querySelector('.process__graphic-body')
    const rows = column.querySelectorAll('.process__graphic-row')
    const columnStartTime = columnIndex * thStagger

    // Set initial states
    if (titleIcon) {
      gsap.set(titleIcon, { scale: 1 })
    }
    if (body) {
      gsap.set(body, { height: 0 })
    }
    gsap.set(rows, { borderBottomColor: 'transparent' })

    rows.forEach(row => {
      const icons = row.querySelectorAll('.process__graphic-item-icon, .process__graphic-item-icon-image')
      gsap.set(icons, { opacity: 1, scale: 1 })
    })

    // TH: column slides in
    tl.to(column, {
      y: 0,
      opacity: 1,
      duration: 0.8,
      ease: 'power2.out'
    }, columnStartTime)

    // TH: arrow
    if (title) {
      tl.to(title, {
        '--arrow-opacity': 1,
        '--arrow-translate-x': 0,
        duration: 0.5,
        ease: 'power2.out'
      }, columnStartTime + 0.2)
    }

    // Phase 2: TBODYs start right as last TH lands
    if (body) {
      tl.to(body, {
        opacity: 1,
        height: 'auto',
        duration: 0.6,
        ease: 'power2.out'
      }, bodyStartTime)
    }

    if (rows.length > 0) {
      tl.to(rows, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out'
      }, bodyStartTime + 0.2)

      tl.to(rows, {
        borderBottomColor: '#E8EBF5',
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out'
      }, bodyStartTime + 0.4)
    }
  })

  // Background opacity - starts when tbodys appear
  tl.to(processGraphic, {
    '--background-opacity': 1,
    duration: 0.6,
    ease: 'power2.out'
  }, bodyStartTime + 0.2)

  // Cleanup: Restore natural height after all animations complete
  const totalAnimationDuration = bodyStartTime + 1.4
  tl.to(processGraphic, {
    height: 'auto',
    duration: 0.1,
    ease: 'none'
  }, totalAnimationDuration)
}

// =============================================================================
// Process Graphic Animations - Desktop (Paired variant, kept for reference)
// Each thead/tbody pair animates together, staggered from the previous pair
// To use: swap processAnimations() call with processAnimationsPaired()
// =============================================================================
export function processAnimationsPaired() {
  const processGraphic = document.querySelector('.process__graphic')
  const processColumns = document.querySelectorAll('.process__graphic-column')

  if (processColumns.length === 0) return

  const originalHeight = processGraphic.offsetHeight
  gsap.set(processGraphic, { height: originalHeight })

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: '.process__graphic',
      start: 'top 75%',
      toggleActions: 'play none none none'
    }
  })

  const pairStagger = 0.5
  const bodyFollowDelay = 0.3

  processColumns.forEach((column, columnIndex) => {
    const title = column.querySelector('.process__graphic-title')
    const titleIcon = column.querySelector('.process__graphic-title-icon')
    const body = column.querySelector('.process__graphic-body')
    const rows = column.querySelectorAll('.process__graphic-row')
    const pairStartTime = columnIndex * pairStagger

    if (titleIcon) { gsap.set(titleIcon, { scale: 1 }) }
    if (body) { gsap.set(body, { height: 0 }) }
    gsap.set(rows, { borderBottomColor: 'transparent' })

    rows.forEach(row => {
      const icons = row.querySelectorAll('.process__graphic-item-icon, .process__graphic-item-icon-image')
      gsap.set(icons, { opacity: 1, scale: 1 })
    })

    tl.to(column, { y: 0, opacity: 1, duration: 0.8, ease: 'power2.out' }, pairStartTime)

    if (title) {
      tl.to(title, { '--arrow-opacity': 1, '--arrow-translate-x': 0, duration: 0.5, ease: 'power2.out' }, pairStartTime + 0.2)
    }

    const bodyStart = pairStartTime + bodyFollowDelay
    if (body) {
      tl.to(body, { opacity: 1, height: 'auto', duration: 0.6, ease: 'power2.out' }, bodyStart)
    }
    if (rows.length > 0) {
      tl.to(rows, { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: 'power2.out' }, bodyStart + 0.2)
      tl.to(rows, { borderBottomColor: '#E8EBF5', duration: 0.6, stagger: 0.1, ease: 'power2.out' }, bodyStart + 0.4)
    }
  })

  tl.to(processGraphic, { '--background-opacity': 1, duration: 0.6, ease: 'power2.out' }, bodyFollowDelay + 0.2)

  const totalAnimationDuration = (processColumns.length - 1) * pairStagger + bodyFollowDelay + 1.4
  tl.to(processGraphic, { height: 'auto', duration: 0.1, ease: 'none' }, totalAnimationDuration)
}

// =============================================================================
// Process Graphic Animations - Mobile
// =============================================================================
export function processAnimationsMobile() {
  const processGraphic = document.querySelector('.process__graphic')
  const processColumns = document.querySelectorAll('.process__graphic-column')

  if (processColumns.length === 0) return

  // Calculate and preserve the total height to prevent layout shift
  const originalHeight = processGraphic.offsetHeight
  gsap.set(processGraphic, { height: originalHeight })

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: '.process__graphic',
      start: 'top 80%',
    }
  })

  // Columns
  processColumns.forEach((column, index) => {
    const titleIcon = column.querySelector('.process__graphic-title-icon')
    const arrow = column.querySelector('.process__graphic-arrow')
    const staggerDelay = index * 0.2

    // Set title icon to appear immediately with column (no separate scale animation)
    // Preserve translateY transform for vertical centering
    if (titleIcon) {
      gsap.set(titleIcon, { scale: 1, yPercent: -50 })
    }

    // Columns
    tl.to(column, {
      y: 0,
      opacity: 1,
      duration: 0.8,
      ease: 'power2.out'
    }, staggerDelay)

    // Arrows
    if (arrow) {
      tl.to(arrow, {
        opacity: 1,
        duration: 0.5,
        ease: 'power2.out'
      }, staggerDelay + 0.4)
    }
  })

  // Bodies and rows
  // Last column starts at (n-1)*0.2, its animation is 0.8s, so it's mostly visible by ~0.6s in
  const lastColumnStart = (processColumns.length - 1) * 0.2
  const detailDelay = 0.3 // Short pause after last header is mostly visible

  processColumns.forEach((column) => {
    const body = column.querySelector('.process__graphic-body')
    const rows = column.querySelectorAll('.process__graphic-row')
    const bodyStartTime = lastColumnStart + 0.5 + detailDelay

    gsap.set(body, { height: 0 })
    gsap.set(rows, { borderBottomColor: 'transparent', opacity: 0, y: -20 })
    
    // Ensure journey icons are visible and don't animate separately
    // They should appear together with their containing row
    rows.forEach(row => {
      const icons = row.querySelectorAll('.process__graphic-item-icon, .process__graphic-item-icon-image')
      gsap.set(icons, { opacity: 1, scale: 1 })
    })

    // Bodies
    if (body) {
      tl.to(body, {
        opacity: 1,
        height: 'auto',
        duration: 0.6,
        ease: 'power2.out'
      }, bodyStartTime)
    }

    // Background opacity
    tl.to(processGraphic, {
      '--background-opacity': 1,
      duration: 0.6,
      ease: 'power2.out'
    }, bodyStartTime + 0.4)

    // Rows (icons come in together with rows, no separate animation)
    if (rows.length > 0) {
      tl.to(rows, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out'
      }, bodyStartTime + 0.2)

      tl.to(rows, {
        borderBottomColor: '#E8EBF5',
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out'
      }, bodyStartTime + 0.2)
    }
  })

  // Cleanup: Restore natural height after all animations complete
  const totalAnimationDuration = lastColumnStart + 0.5 + detailDelay + 1.4 // Add buffer for last animation
  tl.to(processGraphic, {
    height: 'auto',
    duration: 0.1,
    ease: 'none'
  }, totalAnimationDuration)
}

